"use client";

import { joinTournament } from "@/actions/team";
import { useAuth } from "@/contexts/AuthContext";
import { logTournamentEvent } from "@/lib/analytics";
import { Tournament } from "@/types";
import { Button, Form, Input } from "@heroui/react";
import { addToast } from "@heroui/toast";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

interface JoinTournamentFormProps {
  tournament: Tournament;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function JoinTournamentForm({
  tournament,
  onSuccess,
  onCancel,
}: JoinTournamentFormProps) {
  const t = useTranslations();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);

  // Generate default team name from user's name
  const defaultTeamName = useMemo(() => {
    if (user?.name) {
      return t("tournaments.join.form.defaultTeamName", { name: user.name });
    }
    return "";
  }, [user?.name, t]);

  // Form state
  const [teamName, setTeamName] = useState("");
  const [hasEditedName, setHasEditedName] = useState(false);

  // Set default team name when user data becomes available (and user hasn't manually edited)
  useEffect(() => {
    if (defaultTeamName && !hasEditedName && !teamName) {
      setTeamName(defaultTeamName);
    }
  }, [defaultTeamName, hasEditedName, teamName]);

  // Track when user manually edits the name
  const handleTeamNameChange = (value: string) => {
    setTeamName(value);
    setHasEditedName(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);

      const result = await joinTournament({
        tournamentId: tournament.id,
        teamName,
        inviteEmails: [],
      });

      if (!result.success) {
        addToast({
          title: t("tournaments.join.errors.failed"),
          description: result.error,
          color: "danger",
          severity: "danger",
        });
        return;
      }

      // Refresh user data to pick up the new tournament in tournamentIds
      await refreshUser();

      // Invalidate userTeam query to update the navbar "My Team" link
      queryClient.invalidateQueries({ queryKey: ["userTeam", tournament.id] });

      addToast({
        title: t("tournaments.join.success.title"),
        description: t("tournaments.join.success.description"),
        color: "success",
        severity: "success",
      });

      logTournamentEvent("create", tournament.id);

      onSuccess?.();

      // Redirect to "My Team" page
      router.push(`/tournaments/${tournament.slug}/my-team`);
    } catch (error) {
      console.error("Error joining tournament:", error);
      addToast({
        title: t("tournaments.join.errors.failed"),
        color: "danger",
        severity: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const isValidName = teamName.length >= 2 && teamName.length <= 30;
  const canSubmit = isValidName && !loading;

  return (
    <Form className="flex flex-col gap-5" onSubmit={handleSubmit}>
      {/* Team Name Section */}
      <div className="space-y-4 w-full flex flex-col">
        <Input
          isRequired
          label={t("teams.name")}
          labelPlacement="outside"
          placeholder={t("tournaments.join.form.teamNamePlaceholder")}
          value={teamName}
          onValueChange={handleTeamNameChange}
          isInvalid={!isValidName && teamName.length > 0}
          errorMessage={
            !isValidName && teamName.length > 0
              ? t("tournaments.join.form.teamNameInvalid")
              : undefined
          }
          size="lg"
          variant="flat"
          maxLength={30}
          classNames={{
            inputWrapper:
              "bg-white/5 border border-white/10 hover:bg-white/10 group-data-[focus=true]:bg-white/10",
          }}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex sm:flex-row gap-2 pt-3 border-t border-white/10">
        {onCancel && (
          <Button
            variant="bordered"
            onPress={onCancel}
            isDisabled={loading}
            size="lg"
          >
            {t("common.cancel")}
          </Button>
        )}

        <Button
          color="primary"
          type="submit"
          isDisabled={!canSubmit}
          isLoading={loading}
          size="lg"
        >
          {t("tournaments.join.form.submitButton")}
        </Button>
      </div>
    </Form>
  );
}
